export interface PagedResponse<T> {
  totalCount: number;
  items: T[];
}

export interface ApiResponse<T> {
  items: T;
  itemsReturned: number;
  totalItemsFromQuery: number;
}

export interface ApiErrorResponse {
  statusCode: number;
  data?: any;
  detail: object;
}

export interface UpdateResult {
  error: boolean;
  response: ApiErrorResponse;
}

export interface BeaconBase {
  serial: string;
  beaconId: string;
  hwv: string;
  isOn: boolean;
  alerts: Alert[];
  position: Position;
  batt: BatteryBase | BatteryWithTimeStamp;
}
export interface Beacon extends BeaconBase {
  boardType: number;
  isEnabled: boolean;
  org: Organization;
  project: ProjectBase;
  site: SiteBase;
  iccid: string;
  imsi: string;
  fwv: string;
  gfwv: string;
  mfwv: string;
  lastMsgAt: Date;
  sensors: BeaconSensors;
}

interface BeaconSensors {
  accel: Accelerometer;
  batt: BatteryWithTimeStamp;
}

export interface BatteryBase {
  v: number;
  hoursLeft: number;
  percentLeft: number;
}

export interface BatteryWithTimeStamp extends BatteryBase {
  ts: Date;
}
interface PositionBase {
  lat: number;
  lon: number;
  cprs: number;
}

export interface Position extends PositionBase {
  ts: Date;
  alt: number;
  acc: Accuracy;
  dop: Dop;
  sats: number;
}

interface Dop {
  g?: number;
  p?: number;
  h: number;
  v?: number;
  t?: number;
}

interface Accuracy {
  h: number;
  v?: number;
  t?: number;
}

export interface Alert {
  alert: string;
  ts: Date;
  descr: string;
}
interface Accelerometer {
  x: number;
  y: number;
  z: number;
}

export interface UpdateEvent {
  event: string;
  ts: Date;
}

export interface BeaconPositionEvent {
  ts: Date;
  position: Position;
}

export interface BeaconSensorEvent {
  ts: Date;
  sensors: BeaconSensors;
}

interface ProjectBase {
  id: string;
  name: string;
  createdAt: Date;
  isModifiedByUser: boolean;
  address: Address;
}
interface ProjectWithArea extends ProjectBase {
  area: Area[] | null;
}

export interface Project extends ProjectWithArea {
  org: Organization;
  totalSites: number;
  sites: SiteWithBeacons[];
}

interface SiteBase {
  id: string;
  name: string;
  createdAt: string | Date;
  isModifiedByUser: boolean;
  address: Address;
}

export interface SiteWithBeacons extends SiteBase {
  area: Area[] | null;
  totalBeacons: number;
  beacons: BeaconBase[];
}

interface Site extends SiteWithBeacons {
  project: ProjectBase;
  org: Organization;
}

export interface Organization {
  id: string;
  name: string;
}

interface Area {
  lat: number;
  lon: number;
}

interface Address {
  postalCode: string | null;
  city: string | null;
  street: string | null;
}

export interface User {
  id: string;
  email: string;
  org: Organization;
  sendAlerts: boolean;
  sendMotionDetected: boolean;
  dataConsumer: DataConsumer | null;
}

export type LinearRing = {
  lat: number;
  lon: number;
}[];

export enum ForwardToPlatform {
  NONE = 0,
  UDP = 1,
  MOVEMENT_MAIL_ALERTING = 2,
}

export enum BeaconSortingColumn {
  Serial = 0,
  OnOffSwitch = 1,
  BatteryVoltage = 2,
  ConstructionProjectName = 3,
  ConstructionSiteName = 4,
  OrganizationName = 5,
  LastMessageReceivedAt = 6,
}

export enum SCBOneDevicesSortingColumn {
  Serial = 0,
  OnOffSwitch = 1,
  OrganizationName = 2,
  LastMessageReceivedAt = 3,
}

export enum SortingOrder {
  Ascending = 0,
  Descending = 1,
}

export interface BeaconTableSorting {
  sortColumnId: BeaconSortingColumn | undefined;
  sortOrderId: SortingOrder | undefined;
}

export interface SCBOneDevicesTableSorting {
  sortColumnId: SCBOneDevicesSortingColumn | undefined;
  sortOrderId: SortingOrder | undefined;
}

export enum BeaconTableColumns {
  Serial = "Serial",
  Status = "OnOffSwitch",
  Iccid = "Iccid",
  Hwv = "Hwv",
  Fwv = "Fwv",
  ConstructionProjectName = "ConstructionProjectName",
  ConstructionSiteName = "ConstructionSiteName",
  OrganizationName = "OrganizationName",
  LastMessageReceivedAt = "LastMessageReceivedAt",
  ConfigurationButton = "ConfigurationButton",
  OmmitColumn = "OmmitColumn",
}

export type ConstructionProjectMutableProperties = {
  name: string | {};
  address?: Address | {};
  sites?: string[] | {};
};

export type ConstructionSiteMutableProperties = Pick<Site, "name">;

export type OrganizationMutableProperties = Pick<Organization, "name">;

export enum Trigger {
  TIME = 1,
  MOVE = 2,
  BATTERY_LOW = 4,
  POWER_ON = 8,
  POWER_OFF = 16,
}

export function isOn(trigger: Trigger) {
  return (trigger & Trigger.POWER_ON) === Trigger.POWER_ON && !isOff(trigger);
}

export function isOff(trigger: Trigger) {
  return (trigger & Trigger.POWER_OFF) === Trigger.POWER_OFF;
}

export function isHeartbeat(trigger: Trigger) {
  return (trigger & Trigger.TIME) === Trigger.TIME && !isOff(trigger);
}

export function isMovement(trigger: Trigger) {
  return (trigger & Trigger.MOVE) === Trigger.MOVE && !isOff(trigger);
}

export interface Measurement {
  measuredAt: Date;
  reason: Trigger;
}

export interface BeaconEnvironment extends Measurement {
  value: {
    batt: {
      v: number;
    };
    cell: {
      iccid: string;
    };
    remainingRuntimeHours: number;
    remainingRuntimeRatio: number;
  };
}

export type CorrectionServiceType = "NTRIP" | "PointPerfect";

export type MarkerColor = "blue" | "green" | "red" | "orange" | "gray";

export enum SensorTypes {
  NTRIP = "NTRIP",
  PointPerfect = "PointPerfect",
}

export enum BoardType {
  STM_PLATFORM = 1,
  NORDIC_PLATFORM = 2,
}

export interface BeaconConfigFile {
  [key: string]: number | string | boolean;
}

export interface GetBeaconConfigurationStateResponse {
  desired: DesiredBeaconConfig;
  reported: ReportedBeaconConfig;
}

export interface GetSCBOneConfigurationStateResponse {
  desired: DesiredSCBOneConfig;
  reported: ResportedSCBOneConfig;
}

type ReportedBeaconConfig = DesiredBeaconConfig;

type ResportedSCBOneConfig = DesiredSCBOneConfig;

export interface DesiredBeaconConfig {
  activityInterval: number;
  inactivityInterval: number;
  heartbeat: number;
  ignoreMotion: number;
  accelerometer: ConfigAccelerometer;
  rtk: Rtk;
  positioning: ConfigPositioning;
}

export interface DesiredSCBOneConfig {
  rtk: Rtk;
  positioning: ConfigPositioning;
  continuousPositioning: ConfigContinuousPositioning;
}

interface ConfigContinuousPositioning {
  refreshSchedule: number;
  refreshTimeout: number;
  maxSamples: number;
}

interface ConfigPositioning {
  heartbeat: number;
  ttffTimeout: number;
  ttffRetries: number;
  fixQualityTimeout: number;
  dynamicModel: number;
}

interface Rtk {
  protocol: string;
  credentials: Credentials;
}

interface Credentials {
  host: string;
  port: number;
  mountpoint: string;
  user: string;
  password: string;
}

interface ConfigAccelerometer {
  duration: number;
  threshold: number;
  x: boolean;
  y: boolean;
  z: boolean;
}

export enum DynamicModel {
  PORTABLE = 0,
  STATIONARY = 2,
  PEDESTRIAN = 3,
  AUTOMOTIVE = 4,
  ROBOTIC_MOWER = 11,
}

export interface SCBOneDeviceBase {
  id: string;
  serial: string;
}

export interface SCBOneDevice extends SCBOneDeviceBase {
  isEnabled: boolean;
  org: Organization;
  iccid: string;
  imei: string;
  imsi: string;
  fwv: string;
  gfwv: string;
  mfwv: string;
  hwv: string;
  lastMsgAt: Date;
  isOn: boolean;
  alerts: Alert[];
}

export interface SCBOneOperationRun {
  id: string;
  org: Organization;
  device: SCBOneDeviceBase;
  startDatetime: Date;
  endDatetime: Date | null;
}

export interface SCBOneDeviceOrganization extends SCBOneDeviceBase {
  isEnabled: boolean;
  isOn: boolean;
  ts: Date;
}

export interface DataConsumerBase {
  id: string;
  name: string;
}

export interface Contact {
  id: string;
  name: string;
  email: string;
}
export interface DataConsumer extends DataConsumerBase {
  organization: Organization;
  status: "Pending" | "Approved" | "Denied";
  disabled: boolean;
  public: boolean;
  contacts: Contact[] | [];
}

export const IntegrationProtocol = {
  HTTP: "HTTP",
  EMAIL: "EMAIL",
} as const;

export type IntegrationProtocol = keyof typeof IntegrationProtocol;

export const IntegrationMethod = {
  POST: "POST",
  PUT: "PUT",
} as const;

export type IntegrationMethod = keyof typeof IntegrationMethod;

export const AuthType = {
  Basic: "Basic",
  Bearer: "Bearer",
} as const;

export type AuthType = keyof typeof AuthType;

export const EncodingType = {
  UTF8: "utf-8",
  BASE64: "base64",
} as const;

export type EncodingType = (typeof EncodingType)[keyof typeof EncodingType];

export const ContentType = {
  APPXML: "application/xml",
  JSON: "application/json",
  TEXTXML: "text/xml",
} as const;

export type ContentType = (typeof ContentType)[keyof typeof ContentType];

export interface Integration {
  id: string;
  name: string;
  consumer: DataConsumerBase;
  protocol: IntegrationProtocol;
  endpoint: string | null;
  method: IntegrationMethod | null;
  auth: IntegrationAuth;
  events: IntegrationEvents[] | [];
}

export type DataConsumerMutableProperties = {
  name?: string | {};
  status?: string | {};
  disabled?: boolean | {};
  public?: boolean | {};
};

export type DataConsumerContactMutableProperties = {
  id?: string | {};
  name?: string | {};
  email?: string | {};
};

export type IntegrationAuth = {
  authType: AuthType;
  authValue: string;
  encoding: EncodingType;
};

export enum IntegrationEvents {
  ON = 1,
  OFF = 2,
  POSITION = 5,
}

export type IntegrationMutableProperties = {
  name?: string | {};
  consumerId?: string | {};
  protocol?: IntegrationProtocol | {};
  endpoint?: string | null | {};
  method?: IntegrationMethod | {};
  auth?: IntegrationAuth | {};
  events: IntegrationEvents[] | [];
};
export interface IntegrationFormData {
  endpointUrl: string | null;
  integrationName: string;
  integrationProtocol: IntegrationProtocol;
  httpMethod?: string;
  authType?: string;
  authValue?: string;
  authEncoding?: EncodingType;
  events: number[];
  templateContent?: string;
  templateContentType?: ContentType;
}
export interface IntegrationTemplate {
  content: string;
  contentType: ContentType;
  encoding: EncodingType;
}

export enum IntegrationSortField {
  DateCreated = 0,
  Name = 1,
}

export interface IntegrationSortValues {
  sort: IntegrationSortField;
  order: SortingOrder;
}
